import { useEffect, useState, useMemo, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useTrashRankings, RankedPlayer } from '@/hooks/useTrashRankings';
import { useScoringRules } from '@/contexts/ScoringRulesContext';
import { useAddDrop } from '@/hooks/useAddDrop';
import { useLeague } from '@/contexts/LeagueContext';
import { buildScoringWeights, calculateTrashPoints } from '@/lib/trashScoring';
import MLBPlayerAvatar from '@/components/mlb-beta/MLBPlayerAvatar';
import {
  Crown, Skull, ArrowRight, Search, X, TrendingUp,
  TrendingDown, Minus, GitCompareArrows,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface PlayerCompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected player A (e.g. from roster or player page) */
  initialPlayerIdA?: string | null;
  /** Pre-selected player B */
  initialPlayerIdB?: string | null;
}

interface PlayerInfo {
  id: string;
  full_name: string;
  position_abbreviation: string;
  mlb_team_abbreviation: string | null;
  headshot_url: string | null;
  status: string;
}

interface GameStatRow {
  game_date: string;
  trash_points: number;
}

const PlayerCompareDialog = ({
  open,
  onOpenChange,
  initialPlayerIdA,
  initialPlayerIdB,
}: PlayerCompareDialogProps) => {
  const { scoringRules } = useScoringRules();
  const { rankings } = useTrashRankings(scoringRules);
  const addDrop = useAddDrop();
  const { activeLeague } = useLeague();
  const scoringWeights = useMemo(() => buildScoringWeights(activeLeague?.category_config), [activeLeague?.category_config]);

  const [playerIdA, setPlayerIdA] = useState<string | null>(initialPlayerIdA || null);
  const [playerIdB, setPlayerIdB] = useState<string | null>(initialPlayerIdB || null);
  const [playerInfoA, setPlayerInfoA] = useState<PlayerInfo | null>(null);
  const [playerInfoB, setPlayerInfoB] = useState<PlayerInfo | null>(null);
  const [gameStatsA, setGameStatsA] = useState<GameStatRow[]>([]);
  const [gameStatsB, setGameStatsB] = useState<GameStatRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchingSlot, setSearchingSlot] = useState<'A' | 'B' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PlayerInfo[]>([]);
  const [searching, setSearching] = useState(false);

  // Sync initial props
  useEffect(() => {
    if (open) {
      if (initialPlayerIdA) setPlayerIdA(initialPlayerIdA);
      if (initialPlayerIdB) setPlayerIdB(initialPlayerIdB);
    }
  }, [open, initialPlayerIdA, initialPlayerIdB]);

  // Fetch player info + recent game stats
  const fetchPlayerData = useCallback(async (playerId: string, slot: 'A' | 'B') => {
    const [pRes, gRes] = await Promise.all([
      supabase.from('players')
        .select('id, full_name, position_abbreviation, mlb_team_abbreviation, headshot_url, status')
        .eq('id', playerId).single(),
      supabase.from('player_game_stats')
        .select('*')
        .eq('player_id', playerId)
        .order('game_date', { ascending: false })
        .limit(10),
    ]);

    // Compute per-game SAB PTS from raw stats + league scoring weights
    const games: GameStatRow[] = (gRes.data || []).map((g: any) => ({
      game_date: g.game_date,
      trash_points: calculateTrashPoints(g as Record<string, number>, scoringWeights),
    }));

    if (slot === 'A') {
      setPlayerInfoA(pRes.data as PlayerInfo | null);
      setGameStatsA(games);
    } else {
      setPlayerInfoB(pRes.data as PlayerInfo | null);
      setGameStatsB(games);
    }
  }, [scoringWeights]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const promises: Promise<void>[] = [];
    if (playerIdA) promises.push(fetchPlayerData(playerIdA, 'A'));
    if (playerIdB) promises.push(fetchPlayerData(playerIdB, 'B'));
    if (promises.length > 0) {
      Promise.all(promises).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [open, playerIdA, playerIdB, fetchPlayerData]);

  // Search players
  useEffect(() => {
    if (!searchingSlot || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      const { data } = await supabase
        .from('players')
        .select('id, full_name, position_abbreviation, mlb_team_abbreviation, headshot_url, status')
        .eq('sport', 'mlb')
        .ilike('full_name', `%${searchQuery}%`)
        .limit(8);
      setSearchResults((data as PlayerInfo[]) || []);
      setSearching(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery, searchingSlot]);

  const rankedA = useMemo(() => rankings.find(r => r.player_id === playerIdA), [rankings, playerIdA]);
  const rankedB = useMemo(() => rankings.find(r => r.player_id === playerIdB), [rankings, playerIdB]);

  const selectPlayer = (player: PlayerInfo) => {
    if (searchingSlot === 'A') {
      setPlayerIdA(player.id);
      setPlayerInfoA(player);
    } else {
      setPlayerIdB(player.id);
      setPlayerInfoB(player);
    }
    setSearchingSlot(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Build comparison rows from scoring rules
  const comparisonStats = useMemo(() => {
    if (!rankedA && !rankedB) return [];
    return scoringRules
      .filter(r => r.isEnabled)
      .map(rule => {
        const valA = rankedA ? getStatValue(rankedA, rule.stat) : 0;
        const valB = rankedB ? getStatValue(rankedB, rule.stat) : 0;
        const ptsA = valA * rule.pointValue;
        const ptsB = valB * rule.pointValue;
        return {
          label: rule.label,
          isNegative: rule.isNegative,
          valA, valB, ptsA, ptsB,
        };
      })
      .filter(s => s.valA > 0 || s.valB > 0);
  }, [rankedA, rankedB, scoringRules]);

  // Last 10 game averages
  const avgA = gameStatsA.length > 0 ? gameStatsA.reduce((s, g) => s + Number(g.trash_points), 0) / gameStatsA.length : 0;
  const avgB = gameStatsB.length > 0 ? gameStatsB.reduce((s, g) => s + Number(g.trash_points), 0) / gameStatsB.length : 0;

  const ownerA = playerIdA ? addDrop.getOwnerTeamName(playerIdA) : null;
  const ownerB = playerIdB ? addDrop.getOwnerTeamName(playerIdB) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="p-5 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <GitCompareArrows className="w-5 h-5 text-primary" />
            Player Comparison
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-80px)]">
          <div className="p-5 space-y-4">

            {/* Player Selector Row */}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-start">
              {/* Player A */}
              <PlayerSlot
                player={playerInfoA}
                ranked={rankedA}
                owner={ownerA}
                isOnMyTeam={playerIdA ? addDrop.isOnMyTeam(playerIdA) : false}
                isFreeAgent={playerIdA ? addDrop.isFreeAgent(playerIdA) : false}
                onSearch={() => { setSearchingSlot('A'); setSearchQuery(''); }}
                loading={loading && !!playerIdA}
              />

              <div className="flex items-center justify-center h-16">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <span className="text-xs font-bold text-muted-foreground">vs</span>
                </div>
              </div>

              {/* Player B */}
              <PlayerSlot
                player={playerInfoB}
                ranked={rankedB}
                owner={ownerB}
                isOnMyTeam={playerIdB ? addDrop.isOnMyTeam(playerIdB) : false}
                isFreeAgent={playerIdB ? addDrop.isFreeAgent(playerIdB) : false}
                onSearch={() => { setSearchingSlot('B'); setSearchQuery(''); }}
                loading={loading && !!playerIdB}
              />
            </div>

            {/* Search overlay */}
            {searchingSlot && (
              <Card className="border-primary/30">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-muted-foreground" />
                    <Input
                      autoFocus
                      placeholder="Search player name..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="h-8 text-sm"
                    />
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setSearchingSlot(null); setSearchQuery(''); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {searching && <Skeleton className="h-8" />}
                  {searchResults.map(p => (
                    <button
                      key={p.id}
                      className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors text-left"
                      onClick={() => selectPlayer(p)}
                    >
                      <MLBPlayerAvatar name={p.full_name} headshotUrl={p.headshot_url} position={p.position_abbreviation} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.full_name}</p>
                        <p className="text-[10px] text-muted-foreground">{p.position_abbreviation} · {p.mlb_team_abbreviation || 'FA'}</p>
                      </div>
                    </button>
                  ))}
                  {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">No players found</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Summary Comparison */}
            {(rankedA || rankedB) && (
              <div className="grid grid-cols-3 gap-2">
                <CompareMetric
                  label="Net Trash Pts"
                  valA={rankedA?.net_points}
                  valB={rankedB?.net_points}
                  formatFn={v => v.toFixed(1)}
                  higherBetter
                />
                <CompareMetric
                  label="Trash 💩"
                  valA={rankedA?.trash_points}
                  valB={rankedB?.trash_points}
                  formatFn={v => v.toFixed(1)}
                  higherBetter
                />
                <CompareMetric
                  label="Anti-Stats ⚡"
                  valA={rankedA?.anti_points}
                  valB={rankedB?.anti_points}
                  formatFn={v => v.toFixed(1)}
                  higherBetter={false}
                />
                <CompareMetric
                  label="Rank"
                  valA={rankedA?.rank}
                  valB={rankedB?.rank}
                  formatFn={v => `#${v}`}
                  higherBetter={false}
                />
                <CompareMetric
                  label="Games"
                  valA={rankedA?.games_played}
                  valB={rankedB?.games_played}
                  formatFn={v => v.toString()}
                  higherBetter
                />
                <CompareMetric
                  label="L10 Avg"
                  valA={gameStatsA.length > 0 ? avgA : undefined}
                  valB={gameStatsB.length > 0 ? avgB : undefined}
                  formatFn={v => v.toFixed(1)}
                  higherBetter
                />
              </div>
            )}

            {/* Stat-by-Stat Breakdown */}
            {comparisonStats.length > 0 && (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50 text-muted-foreground">
                        <th className="text-right px-3 py-2 w-[30%]">
                          {playerInfoA?.full_name?.split(' ').pop() || 'Player A'}
                        </th>
                        <th className="text-center px-2 py-2 w-[40%]">Stat</th>
                        <th className="text-left px-3 py-2 w-[30%]">
                          {playerInfoB?.full_name?.split(' ').pop() || 'Player B'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonStats.map((row, i) => {
                        const aWins = row.isNegative
                          ? row.ptsA < row.ptsB // less negative is better for anti-stats
                          : row.ptsA > row.ptsB;
                        const bWins = row.isNegative
                          ? row.ptsB < row.ptsA
                          : row.ptsB > row.ptsA;
                        const tied = row.ptsA === row.ptsB;

                        return (
                          <tr key={i} className="border-t border-border/50">
                            <td className={cn("text-right px-3 py-1.5 font-mono", aWins && !tied && 'font-bold text-success')}>
                              {row.valA} <span className="text-muted-foreground/60">({row.ptsA >= 0 ? '+' : ''}{row.ptsA.toFixed(1)})</span>
                            </td>
                            <td className="text-center px-2 py-1.5 text-muted-foreground">
                              <span className={cn(row.isNegative ? 'text-destructive/70' : 'text-success/70')}>
                                {row.isNegative ? '⚡' : '💩'}
                              </span>{' '}
                              {row.label}
                            </td>
                            <td className={cn("text-left px-3 py-1.5 font-mono", bWins && !tied && 'font-bold text-success')}>
                              {row.valB} <span className="text-muted-foreground/60">({row.ptsB >= 0 ? '+' : ''}{row.ptsB.toFixed(1)})</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Last 10 Games Sparkline */}
            {(gameStatsA.length > 0 || gameStatsB.length > 0) && (
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-muted-foreground mb-3">Last 10 Games: Trash Points</p>
                  <div className="space-y-2">
                    {playerInfoA && gameStatsA.length > 0 && (
                      <GameSparkRow
                        name={playerInfoA.full_name}
                        games={gameStatsA}
                        color="text-primary"
                      />
                    )}
                    {playerInfoB && gameStatsB.length > 0 && (
                      <GameSparkRow
                        name={playerInfoB.full_name}
                        games={gameStatsB}
                        color="text-accent-foreground"
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {!playerIdA && !playerIdB && (
              <div className="py-12 text-center text-sm text-muted-foreground">
                Select two players to compare their stats side-by-side
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

/** Player selection slot */
const PlayerSlot = ({
  player,
  ranked,
  owner,
  isOnMyTeam,
  isFreeAgent,
  onSearch,
  loading,
}: {
  player: PlayerInfo | null;
  ranked: RankedPlayer | undefined;
  owner: string | null;
  isOnMyTeam: boolean;
  isFreeAgent: boolean;
  onSearch: () => void;
  loading: boolean;
}) => {
  if (loading) return <Skeleton className="h-16 rounded-lg" />;

  if (!player) {
    return (
      <button
        onClick={onSearch}
        className="h-16 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex items-center justify-center gap-2 text-sm text-muted-foreground transition-colors"
      >
        <Search className="w-4 h-4" />
        Select player
      </button>
    );
  }

  return (
    <button
      onClick={onSearch}
      className="p-3 rounded-lg border border-border hover:border-primary/30 text-left transition-colors"
    >
      <div className="flex items-center gap-2">
        <MLBPlayerAvatar name={player.full_name} headshotUrl={player.headshot_url} position={player.position_abbreviation} size="md" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate">{player.full_name}</p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className="text-[9px] px-1 h-4">{player.position_abbreviation}</Badge>
            <span className="text-[10px] text-muted-foreground">{player.mlb_team_abbreviation || 'FA'}</span>
            {isFreeAgent && <Badge variant="outline" className="text-[8px] px-1 h-3.5 text-success border-success/30">FA</Badge>}
            {isOnMyTeam && <Badge variant="secondary" className="text-[8px] px-1 h-3.5 bg-primary/15 text-primary">Mine</Badge>}
            {owner && !isOnMyTeam && <Badge variant="outline" className="text-[8px] px-1 h-3.5">{owner}</Badge>}
          </div>
        </div>
        {ranked && (
          <div className="text-right shrink-0">
            <p className={cn("text-sm font-black", ranked.net_points >= 0 ? 'text-success' : 'text-destructive')}>
              {ranked.net_points.toFixed(1)}
            </p>
            <p className="text-[9px] text-muted-foreground">#{ranked.rank}</p>
          </div>
        )}
      </div>
    </button>
  );
};

/** Single metric comparison cell */
const CompareMetric = ({
  label, valA, valB, formatFn, higherBetter,
}: {
  label: string;
  valA?: number;
  valB?: number;
  formatFn: (v: number) => string;
  higherBetter: boolean;
}) => {
  const aStr = valA !== undefined ? formatFn(valA) : '-';
  const bStr = valB !== undefined ? formatFn(valB) : '-';

  let winner: 'A' | 'B' | null = null;
  if (valA !== undefined && valB !== undefined && valA !== valB) {
    winner = higherBetter ? (valA > valB ? 'A' : 'B') : (valA < valB ? 'A' : 'B');
  }

  return (
    <div className="bg-muted/30 border border-border rounded-lg p-2 text-center">
      <p className="text-[9px] text-muted-foreground uppercase mb-1">{label}</p>
      <div className="flex items-center justify-center gap-2">
        <span className={cn("text-xs font-bold", winner === 'A' && 'text-success')}>
          {aStr}
        </span>
        <span className="text-[9px] text-muted-foreground">vs</span>
        <span className={cn("text-xs font-bold", winner === 'B' && 'text-success')}>
          {bStr}
        </span>
      </div>
    </div>
  );
};

/** Mini spark row for last 10 games */
const GameSparkRow = ({
  name,
  games,
  color,
}: {
  name: string;
  games: GameStatRow[];
  color: string;
}) => {
  const max = Math.max(...games.map(g => Math.abs(Number(g.trash_points))), 1);

  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-muted-foreground w-20 truncate">{name.split(' ').pop()}</span>
      <div className="flex-1 flex items-end gap-0.5 h-6">
        {games.slice().reverse().map((g, i) => {
          const val = Number(g.trash_points);
          const height = Math.max(2, (Math.abs(val) / max) * 24);
          return (
            <div
              key={i}
              className={cn(
                "flex-1 rounded-sm transition-all",
                val >= 0 ? 'bg-success/60' : 'bg-destructive/60'
              )}
              style={{ height: `${height}px` }}
              title={`${format(parseISO(g.game_date), 'MMM d')}: ${val.toFixed(1)} TP`}
            />
          );
        })}
      </div>
      <span className={cn("text-[10px] font-bold w-10 text-right", color)}>
        {(games.reduce((s, g) => s + Number(g.trash_points), 0) / games.length).toFixed(1)}
      </span>
    </div>
  );
};

function getStatValue(ranked: RankedPlayer, stat: string): number {
  const map: Record<string, keyof RankedPlayer> = {
    strikeouts: 'strikeouts', gidp: 'gidp', earned_runs: 'earned_runs',
    blown_saves: 'blown_saves', errors: 'errors', hits: 'hits',
    home_runs: 'home_runs', rbi: 'rbi', stolen_bases: 'stolen_bases',
    pitcher_strikeouts: 'pitcher_strikeouts', pitcher_win: 'pitcher_wins',
    save: 'saves',
  };
  const key = map[stat];
  if (!key) return 0;
  return (ranked[key] as number) || 0;
}

export default PlayerCompareDialog;
